import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { getProductList } from "../../backend/shop/product";
import Product from "../../types/shop/product";
import { sanitizeHTML } from "../../utils/htmlSanitizer";
import { getSeoValue } from "../../backend/seo/seo";
import { Helmet } from "react-helmet-async";

const ROWS_PER_PAGE = 4;

// ─── ImageCarousel ────────────────────────────────────────────────────────────

const ImageCarousel = ({imageLinks}: { imageLinks: string }) => {
  const images: string[] = imageLinks
    .split(",")
    .map((link) => link.trim().split("|")[0])
    .filter((link) => link);

  const alts: string[] = imageLinks
    .split(",")
    .map((link) => (link.trim().includes("|") ? link.trim().split("|")[1] : "Фото товару"))
    .filter((link) => link);

  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () =>
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  const goToImage = (index: number) => setCurrentImageIndex(index);

  if (images.length === 0) {
    return (
      <div style={styles.imageContainer}>
        <div style={styles.noImagePlaceholder}>Немає зображення</div>
      </div>
    );
  }

  if (images.length === 1) {
    return (
      <div style={styles.imageContainer}>
        <img style={styles.productImage} src={images[0]} alt={alts[0]}/>
      </div>
    );
  }

  return (
    <div style={styles.imageContainer}>
      <div style={styles.carouselContainer}>
        <img
          style={styles.productImage}
          src={images[currentImageIndex]}
          alt={alts[currentImageIndex]}
        />
        <button
          style={{ ...styles.navButton, ...styles.prevButton }}
          onClick={prevImage}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.8)")}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.5)")}
        >
          ‹
        </button>
        <button
          style={{ ...styles.navButton, ...styles.nextButton }}
          onClick={nextImage}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.8)")}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.5)")}
        >
          ›
        </button>
        <div style={styles.imageCounter}>
          {currentImageIndex + 1} / {images.length}
        </div>
        <div style={styles.dotsContainer}>
          {images.map((_, index) => (
            <button
              key={index}
              style={{
                ...styles.dot,
                backgroundColor: index === currentImageIndex ? "var(--orange)" : "#ccc",
              }}
              onClick={() => goToImage(index)}
              onMouseOver={(e) => {
                if (index !== currentImageIndex) e.currentTarget.style.backgroundColor = "#999";
              }}
              onMouseOut={(e) => {
                if (index !== currentImageIndex) e.currentTarget.style.backgroundColor = "#ccc";
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── ProductCard ──────────────────────────────────────────────────────────────

const ProductCard = ({product, showDivider}: { product: Product; showDivider: boolean }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      gap: "1rem",
    }}
  >
    <div style={{display: "flex", flexDirection: "column", gap: "1rem"}}>
      <ImageCarousel imageLinks={product.imageLink}/>
      <a
        style={styles.orderButton}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "var(--orange)")}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "var(--white)")}
        href={product.shopLink}
        target="_blank"
      >
        Замовити у перевіреного виробника
      </a>
    </div>

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <h2
        style={{fontSize: "1.5rem", fontWeight: "bold"}}
        dangerouslySetInnerHTML={{__html: sanitizeHTML(product.name)}}
      />
      <p style={{fontSize: "2rem", fontWeight: "bold", color: "var(--orange)"}}>
        {Math.round(product.price)} грн
      </p>
    </div>
    {showDivider && <hr style={styles.divider}/>}
  </div>
);

// ─── InlineCheckbox ───────────────────────────────────────────────────────────

const InlineCheckbox = ({state, onClick}: { state: boolean; onClick: () => void }) => (
  <div
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      width: "24px",
      height: "24px",
      border: "2px solid var(--black)",
      backgroundColor: state ? "var(--orange)" : "transparent",
      cursor: "pointer",
      flexShrink: 0,
      transition: "background-color 0.15s",
    }}
  />
);

// ─── FilterBar ────────────────────────────────────────────────────────────────

const FilterBar = ({
  categories,
  filters,
  selectedCategory,
  activeFilters,
  onCategoryChange,
  onFilterToggle,
}: {
  categories: string[];
  filters: string[];
  selectedCategory: string | null;
  activeFilters: Set<string>;
  onCategoryChange: (cat: string | null) => void;
  onFilterToggle: (filter: string) => void;
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const currentLabel = selectedCategory ? selectedCategory.toUpperCase() : "ПОКАЗАТИ ВСІ ВИРОБИ";
  const otherCategories = categories.filter((cat) => cat !== selectedCategory);
  const isMobile = useMediaQuery({query: `(max-width: 768px)`});
  const isDouble = useMediaQuery({query: "(max-width: 1400px)"});

  return (
    <>
      <Helmet>
        <title>210 Окремий Штурмовий Полк | Мерч</title>
        <meta name="description"
              content="Офіційна продукція із символікою підрозділу."/>
      </Helmet>
      <div style={{...filterStyles.wrapper, flexDirection: isMobile ? "column" : "row"}}>
        {/* ── Category dropdown ── */}
        <div
          style={{
            ...filterStyles.dropdownRoot,
            width: isMobile
              ? "var(--main-container-width)"
              : "calc(calc(var(--main-container-width) - 2rem)/3)",
          }}
          ref={dropdownRef}
        >
          {/* Trigger */}
          <div style={filterStyles.dropdownTrigger} onClick={() => setDropdownOpen((o) => !o)}>
            <p style={filterStyles.triggerLabel}>{currentLabel}</p>
            <img
              src="/assets/icons/arrow-pag.svg"
              style={{
                width: "1.2rem",
                height: "1.2rem",
                transform: dropdownOpen ? "rotate(90deg)" : "rotate(270deg)",
                transition: "transform 0.2s",
              }}
              alt=""
            />
          </div>

          {/* Floating menu — position:absolute so it never pushes content down */}
          {dropdownOpen && (
            <div style={filterStyles.dropdownMenu}>
              {selectedCategory !== null && (
                <>
                  <div
                    style={filterStyles.dropdownItemRow}
                    onClick={() => {
                      onCategoryChange(null);
                      setDropdownOpen(false);
                    }}
                  >
                    <p style={filterStyles.dropdownItemText}>ПОКАЗАТИ ВСІ ВИРОБИ</p>
                  </div>
                  {otherCategories.length > 0 && <div style={filterStyles.itemDivider}/>}
                </>
              )}

              {otherCategories.map((cat, idx) => (
                <React.Fragment key={cat}>
                  <div
                    style={filterStyles.dropdownItemRow}
                    onClick={() => {
                      onCategoryChange(cat);
                      setDropdownOpen(false);
                    }}
                  >
                    <p style={filterStyles.dropdownItemText}>{cat.toUpperCase()}</p>
                  </div>
                  {idx < otherCategories.length - 1 && <div style={filterStyles.itemDivider}/>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* ── Filter checkboxes ── */}
        <div
          style={{
            ...filterStyles.chipsRow,
            width: isMobile
              ? "var(--main-container-width)"
              : `calc(calc(var(--main-container-width) - ${isDouble ? 1 : 2}rem)/${isDouble ? 2.5 : 3})`,
            display: isMobile ? "grid" : "flex",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "",
          }}
        >
          {filters.map((filter) => {
            const active = activeFilters.has(filter);
            return (
              <div
                key={filter}
                style={filterStyles.checkboxRow}
                onClick={() => onFilterToggle(filter)}
              >
                <InlineCheckbox state={active} onClick={() => onFilterToggle(filter)}/>
                <p
                  style={{...filterStyles.checkboxLabel, fontSize: isMobile ? "1.5rem" : "1.2rem"}}
                >
                  {filter}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────

type Props = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number, ref: any) => void;
};

const ArrowIcon = ({disabled}: { disabled: boolean }) => (
  <svg
    viewBox="0 0 566.9 566.9"
    width="1.5rem"
    height="1.5rem"
    className="arrow-icon"
    style={{
      fill: disabled ? "#e2e2e2" : "#666666",
      transition: "fill 0.2s",
      cursor: disabled ? "default" : "pointer",
    }}
    onMouseOver={(e) => {
      if (!disabled) e.currentTarget.style.fill = "#000000";
    }}
    onMouseOut={(e) => {
      if (!disabled) e.currentTarget.style.fill = "#666666";
    }}
  >
    <path
      d="M195.16,541.02c-7.53,0-14.96-3.47-19.7-10.03L6.16,298.26c-6.2-8.53-6.2-20.08,0-28.61L175.46,36.92c7.89-10.88,23.12-13.28,33.98-5.37,10.88,7.91,13.28,23.12,5.37,33.98L55.92,283.96l158.89,218.43c7.91,10.86,5.51,26.07-5.37,33.98-4.3,3.14-9.31,4.66-14.28,4.66Z"/>
    <path
      d="M541.23,308.29H25.84c-13.45,0-24.33-10.88-24.33-24.33s10.88-24.33,24.33-24.33h515.39c13.45,0,24.33,10.88,24.33,24.33s-10.88,24.33-24.33,24.33Z"/>
  </svg>
);

function Pagination({currentPage, totalPages, onPageChange}: Props) {
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const pages = useMemo(() => Array.from({length: totalPages}, (_, i) => i + 1), [totalPages]);

  if (totalPages <= 1) return null;

  const goToPage = (page: number) => {
    onPageChange(page, paginationRef);
  };

  const isFirst = currentPage === 1;
  const isLast = currentPage === totalPages;

  return (
    <div style={styles.paginationContainer} ref={paginationRef}>
      {/* LEFT ARROW */}
      <button
        style={styles.paginationArrowBtn}
        disabled={isFirst}
        onClick={() => !isFirst && goToPage(currentPage - 1)}
      >
        <ArrowIcon disabled={isFirst}/>
      </button>

      {/* PAGE NUMBERS */}
      {pages.map((page) => {
        const active = page === currentPage;

        return (
          <button
            key={page}
            style={{
              ...styles.pageNumberBtn,
              color: active ? "var(--orange)" : "var(--grey)",
              fontWeight: active ? "bold" : "normal",
            }}
            onClick={() => goToPage(page)}
            className="page-btn"
            onMouseOver={(e) => {
              if (page !== currentPage) e.currentTarget.style.fontWeight = "500";
            }}
            onMouseOut={(e) => {
              if (page !== currentPage) e.currentTarget.style.fontWeight = "normal";
            }}
          >
            {page}
          </button>
        );
      })}

      {/* RIGHT ARROW */}
      <button
        style={{
          ...styles.paginationArrowBtn,
          transform: "rotate(180deg)",
        }}
        disabled={isLast}
        onClick={() => !isLast && goToPage(currentPage + 1)}
      >
        <ArrowIcon disabled={isLast}/>
      </button>
    </div>
  );
}

const ShopPage = () => {
  const isMiddle = useMediaQuery({query: "(max-width: 1400px)"});
  const isSingle = useMediaQuery({query: "(max-width: 700px)"});

  const productsRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const allProducts = getProductList().filter((p) => p.isVisible);

  // Supports both old `category: string` and new `categories: string[]` API shapes
  const getProductCategories = (p: any): string[] =>
    Array.isArray(p.categories) ? p.categories : p.category ? [p.category] : [];

  const categories: string[] = Array.from(
    new Set(allProducts.flatMap((p) => getProductCategories(p))),
  );
  const allFilters: string[] = Array.from(new Set(allProducts.flatMap((p) => p.filters ?? [])));

  // All filters are active by default
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set(allFilters));

  const afterCategory =
    selectedCategory === null
      ? allProducts
      : allProducts.filter((p) => getProductCategories(p).includes(selectedCategory));

  // OR logic: product matches at least one active filter.
  // If all filters are on, also show products with no filters assigned.
  const allFiltersActive = activeFilters.size === allFilters.length;
  const filteredProducts =
    activeFilters.size === 0 || allFiltersActive
      ? afterCategory
      : afterCategory.filter((p) =>
        [...activeFilters].some((f) => (p.filters ?? []).includes(f)),
      );

  const columns = isSingle ? 1 : isMiddle ? 2 : 3;
  const itemsPerPage = ROWS_PER_PAGE * columns;
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleCategoryChange = (cat: string | null) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const handleFilterToggle = (filter: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      next.has(filter) ? next.delete(filter) : next.add(filter);
      return next;
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, _ref: any) => {
    setCurrentPage(page);
    setTimeout(() => {
      productsRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
    }, 0);
  };

  return (
    <div style={styles.formWrapper}>
      {/* Orange header */}
      <div style={{...styles.formWrapper, backgroundColor: "#a7a994"}}>
        <div style={styles.headerContainer}>
          <h1 style={styles.formHeader}>{getSeoValue("shop", "h1")}</h1>
        </div>
      </div>

      {/* Black sub-heading */}
      <div style={{...styles.formWrapper, backgroundColor: "var(--black)"}}>
        <div style={styles.formContainer}>
          <h2 style={styles.subHeading}>{getSeoValue("shop", "description")}</h2>
        </div>
      </div>

      {/* Products + filter */}
      <div style={styles.formContainer} ref={productsRef}>
        <FilterBar
          categories={categories}
          filters={allFilters}
          selectedCategory={selectedCategory}
          activeFilters={activeFilters}
          onCategoryChange={handleCategoryChange}
          onFilterToggle={handleFilterToggle}
        />

        {filteredProducts.length === 0 ? (
          <p style={{marginTop: "2rem", fontSize: "1.2rem", color: "var(--dark-grey)"}}>
            Товарів за обраними фільтрами не знайдено.
          </p>
        ) : (
          <>
            <div
              style={{
                ...styles.formRow,
                gridTemplateColumns: isSingle ? "1fr" : isMiddle ? "1fr 1fr" : "1fr 1fr 1fr",
                marginTop: "2rem",
              }}
            >
              {paginatedProducts.map((item, index) => (
                <ProductCard
                  key={item.id}
                  product={item}
                  showDivider={isSingle && index !== paginatedProducts.length - 1}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* SEO footer text */}
      <div style={styles.formContainer}>
        <h3 style={styles.noteHeading}>Якість та відповідність стандартам</h3>
        <h3 style={styles.noteText}>{getSeoValue("shop", "h2_1")}</h3>
        <h3 style={styles.noteHeading}>Оновлення асортименту</h3>
        <h3 style={styles.noteText}>{getSeoValue("shop", "h2_2")}</h3>
      </div>
    </div>
  );
};

// ─── Filter Styles ────────────────────────────────────────────────────────────

const filterStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "3rem",
    flexWrap: "wrap",
    marginTop: "1rem",
  },

  // ── Dropdown ──
  dropdownRoot: {
    position: "relative", // anchor for the absolute menu
    display: "flex",
    flexDirection: "column",
    minWidth: "330px",
  },
  dropdownTrigger: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    userSelect: "none",
    border: "2px solid var(--black)",
    padding: "1rem 0.5rem",
    gap: "2rem",
  },
  triggerLabel: {
    fontFamily: "UAFSans-Book",
    fontWeight: "bold",
    fontSize: "1rem",
    color: "var(--black)",
    letterSpacing: "0.03em",
  },
  // Floats over the page — does NOT shift any layout
  dropdownMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    minWidth: "100%",
    zIndex: 100,
    display: "flex",
    flexDirection: "column",
    paddingLeft: "0.75rem",
    paddingRight: "0.75rem",
    backgroundColor: "var(--olive)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  },
  dropdownItemRow: {
    paddingTop: "1rem",
    paddingBottom: "1rem",
    cursor: "pointer",
  },
  dropdownItemText: {
    fontFamily: "UAFSans-Book",
    fontSize: "1rem",
    color: "var(--white)",
    letterSpacing: "0.03em",
  },
  itemDivider: {
    width: "100%",
    height: "1.5px",
    backgroundColor: "var(--white)",
    opacity: 0.25,
  },

  // ── Checkboxes ──
  chipsRow: {
    display: "flex",
    flexDirection: "row",
    height: "100%",
    alignItems: "center",
    justifyContent: "space-between",
  },
  checkboxRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "0.75rem",
    cursor: "pointer",
    userSelect: "none",
  },
  checkboxLabel: {
    fontFamily: "UAFSans-Book",
    color: "var(--black)",
    letterSpacing: "0.035em",
  },
};

// ─── Page Styles ──────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  formWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: "var(--white)",
    overflow: "hidden",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    width: "var(--main-container-width)",
    margin: "0 auto",
    paddingTop: "4rem",
    paddingBottom: "2rem",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    width: "var(--main-container-width)",
    margin: "0 auto",
    paddingTop: "2rem",
    paddingBottom: "2rem",
  },
  formHeader: {
    fontSize: "min(4rem,10vw)",
    fontWeight: "bold",
    color: "var(--dark-grey)",
    marginBottom: "1rem",
  },
  subHeading: {
    color: "#a7a994",
    fontSize: "min(2rem,4vw)",
    fontWeight: "bold",
  },
  noteHeading: {
    color: "var(--black)",
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  noteText: {
    color: "var(--black)",
    fontSize: "1.2rem",
    fontWeight: "normal",
    marginBottom: "1rem",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "1rem",
  },
  orderButton: {
    border: "3px solid var(--black)",
    backgroundColor: "white",
    textDecoration: "none",
    fontSize: "1.5rem",
    fontWeight: "bold",
    textAlign: "center",
    padding: "0.5rem",
    color: "var(--black)",
    transition: "all 0.3s linear",
  },
  divider: {
    width: "100%",
    borderColor: "var(--black)",
    marginTop: "1rem",
    marginBottom: "2rem",
  },
  imageContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  carouselContainer: {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  productImage: {
    width: "100%",
    height: "auto",
    display: "flex",
    minHeight: "200px",
    alignItems: "center",
    justifyContent: "center",
  },
  noImagePlaceholder: {
    width: "100%",
    height: "200px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f0f0",
    color: "#666",
    fontSize: "1rem",
    border: "2px dashed #ccc",
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(0,0,0,0.5)",
    color: "white",
    border: "none",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    fontSize: "24px",
    fontWeight: "bold",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.3s ease",
    zIndex: 2,
  },
  prevButton: {left: "10px"},
  nextButton: {right: "10px"},
  imageCounter: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "rgba(0,0,0,0.7)",
    color: "white",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "bold",
  },
  dotsContainer: {
    position: "absolute",
    bottom: "15px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    zIndex: 2,
  },
  dot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
  paginationContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "2rem",
    marginTop: "3rem",
    marginBottom: "1rem",
  },
  paginationArrowBtn: {
    background: "none",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.25rem",
    transition: "color 0.2s ease",
  },
  pageNumberBtn: {
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
  },
};

export default ShopPage;